// src/cockpit4/components/ContentReviewWorkflow.tsx
// ============================================
// P1-功能：内容审核工作流（媒体工作室）
//
// 功能：
//   1. AI 初审（合规检查 / 质量检查 / 品牌一致性 三项检查）
//   2. 人工复审界面（内容预览 / 批注 / 修改建议 / 通过 / 拒绝）
//   3. 修改痕迹和版本管理（批注 + 修改建议留痕，可追溯）
//   4. 审核记录和统计（今日审核数 / 通过率 / 平均耗时 / 超时数）
//   5. 审核 SLA 追踪（默认 24 小时，可调整，倒计时展示）
//
// 数据来源：
//   - RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_packages" })
//     筛选 status === "completed" 但未审核的包作为待审核内容
//   - localStorage（key: xeaos_content_review_records）持久化审核记录
//   - localStorage（key: xeaos_content_review_sla）持久化 SLA 配置
//   - AI 初审规则库（内置：敏感词 / 政治敏感 / 违法违规 / 质量指标 / 品牌关键词）
//   - 无数据时显示空状态"暂无待审核内容"，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileSearch,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  MessageSquare,
  History,
  AlertTriangle,
  ShieldCheck,
  Gauge,
  Sparkles,
  Settings,
  Filter,
  Search,
  X,
  PenLine,
  TrendingUp,
  Timer,
  FileText,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { MediaPackage } from "../../runtime/MediaStudio/types";

// ============================================
// 类型与常量
// ============================================

const RECORDS_STORAGE_KEY = "xeaos_content_review_records";
const SLA_STORAGE_KEY = "xeaos_content_review_sla";
const DEFAULT_SLA_HOURS = 24;

/** 审核状态 */
type ReviewStatus =
  | "pending" // 待审核
  | "ai_reviewing" // AI 初审中
  | "ai_passed" // AI 通过
  | "ai_rejected" // AI 拒绝
  | "human_reviewing" // 人工复审中
  | "approved" // 已通过
  | "rejected"; // 已拒绝

/** AI 检查项状态 */
type CheckStatus = "pass" | "warn" | "fail";

/** AI 单项检查结果 */
interface AiCheckItem {
  name: string;
  status: CheckStatus;
  score: number;
  issues: string[];
  detail: string;
}

/** AI 初审结果（合规 / 质量 / 品牌一致性 三项检查） */
interface AiReviewResult {
  compliance: AiCheckItem;
  quality: AiCheckItem;
  brand: AiCheckItem;
  overallScore: number;
  summary: string;
  reviewedAt: string;
}

/** 批注（修改痕迹） */
interface Annotation {
  id: string;
  field: string;
  content: string;
  suggestion?: string;
  author: string;
  createdAt: string;
}

/** 审核记录 */
interface ReviewRecord {
  id: string;
  packageId: string;
  topic: string;
  platform: string;
  status: ReviewStatus;
  aiReview?: AiReviewResult;
  annotations: Annotation[];
  revisionSuggestion: string;
  /** 驳回原因（选择拒绝时必填，留痕可追溯） */
  rejectReason?: string;
  reviewer: string;
  createdAt: string;
  reviewedAt?: string;
  decision?: "approved" | "rejected";
  durationMs?: number;
}

// ============================================
// AI 初审规则库（内置，非客户数据）
// ============================================

/** 敏感词库（合规检查 - 营销违规词） */
const SENSITIVE_WORDS = [
  "第一", "最", "绝对", "保证", "100%", "免费", "中奖", "点击领取",
  "暴力", "色情", "赌博", "诈骗", "传销", "违禁", "秒杀", "限时",
];

/** 政治敏感词库 */
const POLITICAL_SENSITIVE = ["国家领导人", "政治敏感", "反动", "颠覆"];

/** 违法违规词库 */
const ILLEGAL_WORDS = ["假货", "盗版", "走私", "偷税", "漏税", "行贿", "受贿"];

/** 品牌关键词库（品牌一致性 - 关键词匹配） */
const BRAND_KEYWORDS = ["XEAOS", "企业操作系统", "AI", "智能", "自动化", "知识", "运营"];

/** 品牌风格词库（品牌一致性 - 风格匹配） */
const BRAND_STYLE_WORDS = ["专业", "高效", "创新", "可靠", "一站式", "数字化"];

// ============================================
// 状态展示配置
// ============================================

const STATUS_LABEL: Record<ReviewStatus, string> = {
  pending: "待审核",
  ai_reviewing: "AI初审中",
  ai_passed: "AI通过",
  ai_rejected: "AI拒绝",
  human_reviewing: "复审中",
  approved: "已通过",
  rejected: "已拒绝",
};

const STATUS_COLOR: Record<ReviewStatus, string> = {
  pending: "#9CA3AF",
  ai_reviewing: "#38BDF8",
  ai_passed: "#34D399",
  ai_rejected: "#F87171",
  human_reviewing: "#FBBF24",
  approved: "#34D399",
  rejected: "#F87171",
};

const CHECK_STATUS_COLOR: Record<CheckStatus, string> = {
  pass: "#34D399",
  warn: "#FBBF24",
  fail: "#F87171",
};

const CHECK_STATUS_LABEL: Record<CheckStatus, string> = {
  pass: "通过",
  warn: "警告",
  fail: "不通过",
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** YYYY-MM-DD */
function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/** 平台识别（依据包内脚本类型推断发布平台） */
function detectPlatform(pkg: MediaPackage): string {
  if (pkg.douyinScript) return "抖音";
  if (pkg.xiaohongshuScript) return "小红书";
  if (pkg.bilibiliScript) return "B站";
  if (pkg.liveStreamScript) return "直播";
  if (pkg.articleScript) return "公众号";
  return "通用";
}

/** 提取媒体包内的脚本正文（用于 AI 初审与预览） */
function extractScriptContent(pkg: MediaPackage): string {
  return (
    pkg.articleScript?.content ||
    pkg.videoScript?.content ||
    pkg.xiaohongshuScript?.content ||
    pkg.bilibiliScript?.content ||
    pkg.douyinScript?.content ||
    pkg.liveStreamScript?.content ||
    pkg.description ||
    ""
  );
}

/**
 * 提取 RuntimeManager.query 返回的媒体包列表
 * 兼容多种包装结构（data 数组 / data.packages / packages），避免运行时边界差异导致空数据
 */
function extractPackagesFromResp(resp: unknown): MediaPackage[] {
  try {
    const r = resp as { data?: unknown } | unknown;
    // RuntimeManager.query 包装：{ data: <MediaStudioResult> }
    const inner = (r as { data?: unknown })?.data ?? r;
    const d = (inner as { data?: unknown; packages?: unknown })?.data;
    if (Array.isArray(d)) return d as MediaPackage[];
    if (d && Array.isArray((d as { packages?: unknown }).packages)) {
      return (d as { packages: MediaPackage[] }).packages;
    }
    if (Array.isArray((inner as { packages?: unknown })?.packages)) {
      return (inner as { packages: MediaPackage[] }).packages;
    }
    return [];
  } catch {
    return [];
  }
}

/** SLA 剩余时间计算 */
function slaRemaining(
  createdAt: string,
  slaHours: number
): { ms: number; overdue: boolean; label: string } {
  const created = new Date(createdAt).getTime();
  const deadline = created + slaHours * 3600 * 1000;
  const now = Date.now();
  const ms = deadline - now;
  const overdue = ms < 0;
  const absMs = Math.abs(ms);
  const hours = Math.floor(absMs / 3600000);
  const mins = Math.floor((absMs % 3600000) / 60000);
  const label = overdue ? `超时 ${hours}h${mins}m` : `${hours}h${mins}m`;
  return { ms, overdue, label };
}

/** 格式化耗时（毫秒 -> 可读文本） */
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0m";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const restMins = mins % 60;
  return `${hours}h${restMins}m`;
}

// ============================================
// AI 初审引擎
// ============================================

/** 执行 AI 初审（合规 / 质量 / 品牌一致性 三项检查） */
function runAiReview(pkg: MediaPackage): AiReviewResult {
  const content = extractScriptContent(pkg);
  const topic = pkg.topic || "未命名";

  // ---- 1. 合规检查（敏感词 / 政治敏感 / 违法违规）----
  const foundSensitive = SENSITIVE_WORDS.filter((w) => content.includes(w) || topic.includes(w));
  const foundPolitical = POLITICAL_SENSITIVE.filter((w) => content.includes(w));
  const foundIllegal = ILLEGAL_WORDS.filter((w) => content.includes(w));
  const complianceIssues = [...foundSensitive, ...foundPolitical, ...foundIllegal];
  const complianceScore = Math.max(0, 100 - complianceIssues.length * 20);
  const compliance: AiCheckItem = {
    name: "合规检查",
    status: complianceIssues.length === 0 ? "pass" : complianceScore >= 50 ? "warn" : "fail",
    score: complianceScore,
    issues: complianceIssues,
    detail:
      complianceIssues.length === 0
        ? "未检测到敏感词 / 政治敏感 / 违法违规内容"
        : `检测到 ${complianceIssues.length} 项合规风险：${complianceIssues.slice(0, 5).join("、")}`,
  };

  // ---- 2. 质量检查（字数 / 结构 / 图片完整性）----
  const wordCount = content.length;
  const paragraphs = content.split(/\n\n+/).filter((p) => p.trim().length > 0);
  const hasHeaders = /^#{1,3}\s/m.test(content);
  const hasList = /^[-*]\s/m.test(content);
  const imageCount =
    (pkg.cover ? 1 : 0) +
    (pkg.wechatCover ? 1 : 0) +
    (pkg.xiaohongshuGrid?.length || 0) +
    (pkg.douyinCover ? 1 : 0);
  const hasVideo = !!pkg.video;

  const qualityIssues: string[] = [];
  let qualityScore = 0;
  if (wordCount >= 500) qualityScore += 30;
  else if (wordCount >= 200) qualityScore += 20;
  else {
    qualityScore += 5;
    qualityIssues.push(`字数偏少（${wordCount}字，建议≥200字）`);
  }
  if (paragraphs.length >= 3) qualityScore += 25;
  else if (paragraphs.length >= 1) qualityScore += 15;
  else qualityIssues.push("段落结构不完整");
  if (hasHeaders) qualityScore += 15;
  if (hasList) qualityScore += 10;
  if (imageCount > 0) qualityScore += 20;
  else qualityIssues.push("缺少配图");
  qualityScore = Math.min(100, qualityScore);

  const quality: AiCheckItem = {
    name: "质量检查",
    status: qualityScore >= 70 ? "pass" : qualityScore >= 40 ? "warn" : "fail",
    score: qualityScore,
    issues: qualityIssues,
    detail: `${wordCount}字 · ${paragraphs.length}段 · ${imageCount}张配图 · ${hasVideo ? "含视频" : "无视频"}`,
  };

  // ---- 3. 品牌一致性（关键词匹配 / 风格匹配）----
  const brandMentions = BRAND_KEYWORDS.filter((k) => content.includes(k) || topic.includes(k));
  const styleMentions = BRAND_STYLE_WORDS.filter((k) => content.includes(k));
  const brandScore = Math.min(100, brandMentions.length * 15 + styleMentions.length * 10);
  const brandIssues: string[] = [];
  if (brandMentions.length === 0) brandIssues.push("未提及品牌关键词");
  if (styleMentions.length === 0) brandIssues.push("缺少品牌风格词");

  const brand: AiCheckItem = {
    name: "品牌一致性",
    status: brandScore >= 60 ? "pass" : brandScore >= 30 ? "warn" : "fail",
    score: brandScore,
    issues: brandIssues,
    detail: `品牌关键词 ${brandMentions.length} 个 · 风格词 ${styleMentions.length} 个`,
  };

  const overallScore = Math.round((complianceScore + qualityScore + brandScore) / 3);
  const summary =
    compliance.status === "fail"
      ? `合规检查未通过，存在 ${complianceIssues.length} 项风险，建议人工复审`
      : overallScore >= 70
        ? `AI 初审通过，综合得分 ${overallScore}/100`
        : `AI 初审完成，综合得分 ${overallScore}/100，存在可优化项`;

  return {
    compliance,
    quality,
    brand,
    overallScore,
    summary,
    reviewedAt: new Date().toISOString(),
  };
}

// ============================================
// localStorage 读写
// ============================================

/** 安全读取审核记录 */
function loadRecords(): ReviewRecord[] {
  try {
    const raw = localStorage.getItem(RECORDS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ReviewRecord[]) : [];
  } catch {
    return [];
  }
}

/** 安全写入审核记录 */
function saveRecords(records: ReviewRecord[]): void {
  try {
    localStorage.setItem(RECORDS_STORAGE_KEY, JSON.stringify(records));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/** 安全读取 SLA 配置 */
function loadSlaHours(): number {
  try {
    const raw = localStorage.getItem(SLA_STORAGE_KEY);
    if (!raw) return DEFAULT_SLA_HOURS;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_SLA_HOURS;
  } catch {
    return DEFAULT_SLA_HOURS;
  }
}

/** 安全写入 SLA 配置 */
function saveSlaHours(hours: number): void {
  try {
    localStorage.setItem(SLA_STORAGE_KEY, String(hours));
  } catch {
    // 忽略
  }
}

// ============================================
// 主组件
// ============================================

export default function ContentReviewWorkflow() {
  const [packages, setPackages] = useState<MediaPackage[]>([]);
  const [records, setRecords] = useState<ReviewRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slaHours, setSlaHours] = useState<number>(DEFAULT_SLA_HOURS);
  const [showSlaConfig, setShowSlaConfig] = useState(false);

  // 详情 Modal
  const [selectedPkg, setSelectedPkg] = useState<MediaPackage | null>(null);

  // 历史搜索 / 筛选
  const [historySearch, setHistorySearch] = useState("");
  const [historyFilter, setHistoryFilter] = useState<ReviewStatus | "all">("all");

  // Modal 内批注表单（修改痕迹）
  const [annoField, setAnnoField] = useState("");
  const [annoContent, setAnnoContent] = useState("");
  const [annoSuggestion, setAnnoSuggestion] = useState("");
  const [revisionSuggestion, setRevisionSuggestion] = useState("");
  const [modalAnnotations, setModalAnnotations] = useState<Annotation[]>([]);
  // 驳回原因（P12 短板2：拒绝必填，留痕可追溯）
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState(false);

  // 加载待审核内容（来自 RuntimeManager）
  const loadPackages = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      // 等待 RuntimeManager 初始化（最多 9 秒）
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setError("RuntimeManager 尚未初始化");
        setLoading(false);
        return;
      }
      if (!RuntimeManager.hasRuntime(RuntimeName.MediaStudio)) {
        setError("媒体工作室运行时未注册");
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_packages" });
      const pkgs = extractPackagesFromResp(resp);
      setPackages(pkgs);
    } catch (err) {
      setError((err as Error).message || "加载待审核内容异常");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // 初次挂载：加载记录 + SLA + 待审核内容
  useEffect(() => {
    setRecords(loadRecords());
    setSlaHours(loadSlaHours());
    void loadPackages();
  }, [loadPackages]);

  // 记录变化时持久化
  useEffect(() => {
    saveRecords(records);
  }, [records]);

  // SLA 变化时持久化
  useEffect(() => {
    saveSlaHours(slaHours);
  }, [slaHours]);

  // ============================================
  // 派生：待审核列表 / 历史记录 / 统计
  // ============================================

  /** 待审核列表：status === "completed" 且尚未审核通过/拒绝的包 */
  const pendingItems = useMemo(() => {
    return packages
      .filter((p) => p.status === "completed")
      .map((p) => {
        const record = records.find((r) => r.packageId === p.id);
        const status: ReviewStatus = record?.status ?? "pending";
        return { pkg: p, record, status };
      })
      .filter((item) => item.status !== "approved" && item.status !== "rejected");
  }, [packages, records]);

  /** 历史记录：已审核（approved / rejected），可搜索可筛选 */
  const historyItems = useMemo(() => {
    const kw = historySearch.trim().toLowerCase();
    return records
      .filter((r) => r.status === "approved" || r.status === "rejected")
      .filter((r) => historyFilter === "all" || r.status === historyFilter)
      .filter(
        (r) =>
          !kw ||
          r.topic.toLowerCase().includes(kw) ||
          r.packageId.toLowerCase().includes(kw) ||
          r.platform.toLowerCase().includes(kw)
      )
      .sort((a, b) => (b.reviewedAt || "").localeCompare(a.reviewedAt || ""));
  }, [records, historyFilter, historySearch]);

  /** 审核统计 */
  const stats = useMemo(() => {
    const today = todayStr();
    const reviewedToday = records.filter((r) => r.reviewedAt && r.reviewedAt.slice(0, 10) === today);
    const approvedCount = records.filter((r) => r.status === "approved").length;
    const totalReviewed = records.filter((r) => r.status === "approved" || r.status === "rejected").length;
    const passRate = totalReviewed > 0 ? Math.round((approvedCount / totalReviewed) * 100) : 0;
    const durations = records
      .filter((r) => r.durationMs && r.durationMs > 0)
      .map((r) => r.durationMs as number);
    const avgDurationMs = durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;
    const overdueCount = pendingItems.filter((item) => slaRemaining(item.pkg.createdAt, slaHours).overdue).length;
    return {
      todayCount: reviewedToday.length,
      passRate,
      avgDurationLabel: formatDuration(avgDurationMs),
      overdueCount,
      totalReviewed,
    };
  }, [records, pendingItems, slaHours]);

  // ============================================
  // 操作：AI 初审 / 人工复审
  // ============================================

  /** 触发单条 AI 初审（模拟 ai_reviewing 中间态） */
  const triggerAiReview = useCallback(async (pkg: MediaPackage) => {
    // 写入 / 更新记录为 ai_reviewing
    setRecords((prev) => {
      const existing = prev.find((r) => r.packageId === pkg.id);
      if (existing) {
        return prev.map((r) =>
          r.packageId === pkg.id ? { ...r, status: "ai_reviewing" as ReviewStatus } : r
        );
      }
      const newRecord: ReviewRecord = {
        id: genId("review"),
        packageId: pkg.id,
        topic: pkg.topic || "未命名",
        platform: detectPlatform(pkg),
        status: "ai_reviewing",
        annotations: [],
        revisionSuggestion: "",
        reviewer: "AI 系统",
        createdAt: pkg.createdAt,
      };
      return [newRecord, ...prev];
    });
    // 模拟 AI 分析耗时
    await new Promise((r) => setTimeout(r, 900));
    const result = runAiReview(pkg);
    const nextStatus: ReviewStatus = result.compliance.status === "fail" ? "ai_rejected" : "ai_passed";
    setRecords((prev) =>
      prev.map((r) => (r.packageId === pkg.id ? { ...r, aiReview: result, status: nextStatus } : r))
    );
  }, []);

  /** 批量 AI 初审（对所有 pending 项顺序执行） */
  const batchAiReview = useCallback(async () => {
    const targets = pendingItems.filter((item) => item.status === "pending").map((item) => item.pkg);
    for (const pkg of targets) {
      // eslint-disable-next-line no-await-in-loop
      await triggerAiReview(pkg);
    }
  }, [pendingItems, triggerAiReview]);

  /** 打开复审 Modal，初始化批注 / 修改建议，并标记进入人工复审 */
  const openReviewModal = useCallback(
    (pkg: MediaPackage) => {
      setSelectedPkg(pkg);
      const record = records.find((r) => r.packageId === pkg.id);
      setModalAnnotations(record?.annotations || []);
      setRevisionSuggestion(record?.revisionSuggestion || "");
      setAnnoField("");
      setAnnoContent("");
      setAnnoSuggestion("");
      setRejectReason(record?.rejectReason || "");
      setRejectError(false);
      // 标记进入人工复审
      if (record && (record.status === "ai_passed" || record.status === "ai_rejected")) {
        setRecords((prev) =>
          prev.map((r) =>
            r.packageId === pkg.id ? { ...r, status: "human_reviewing" as ReviewStatus } : r
          )
        );
      }
    },
    [records]
  );

  /** 添加批注（修改痕迹留痕） */
  const addAnnotation = useCallback(() => {
    if (!annoContent.trim() || !selectedPkg) return;
    const anno: Annotation = {
      id: genId("anno"),
      field: annoField.trim() || "正文",
      content: annoContent.trim(),
      suggestion: annoSuggestion.trim() || undefined,
      author: "人工审核",
      createdAt: new Date().toISOString(),
    };
    setModalAnnotations((prev) => [...prev, anno]);
    setAnnoField("");
    setAnnoContent("");
    setAnnoSuggestion("");
  }, [annoField, annoContent, annoSuggestion, selectedPkg]);

  /** 删除批注 */
  const removeAnnotation = useCallback((id: string) => {
    setModalAnnotations((prev) => prev.filter((a) => a.id !== id));
  }, []);

  /** 提交人工复审决策（通过 / 拒绝），记录耗时与修改痕迹 */
  const submitHumanDecision = useCallback(
    (decision: "approved" | "rejected") => {
      if (!selectedPkg) return;
      const now = new Date().toISOString();
      const existing = records.find((r) => r.packageId === selectedPkg.id);
      const createdAt = existing?.createdAt || selectedPkg.createdAt;
      const createdMs = new Date(createdAt).getTime();
      // 拒绝时强制驳回原因（P12 短板2：Rule03 人工兜底，留痕可追溯）
      const reason = decision === "rejected" ? rejectReason.trim() : "";
      setRecords((prev) => {
        const hasRecord = prev.some((r) => r.packageId === selectedPkg.id);
        if (hasRecord) {
          return prev.map((r) =>
            r.packageId === selectedPkg.id
              ? {
                  ...r,
                  status: decision as ReviewStatus,
                  decision,
                  annotations: modalAnnotations,
                  revisionSuggestion,
                  rejectReason: reason || undefined,
                  reviewer: "人工审核",
                  reviewedAt: now,
                  durationMs: Date.now() - createdMs,
                }
              : r
          );
        }
        const newRecord: ReviewRecord = {
          id: genId("review"),
          packageId: selectedPkg.id,
          topic: selectedPkg.topic || "未命名",
          platform: detectPlatform(selectedPkg),
          status: decision as ReviewStatus,
          decision,
          annotations: modalAnnotations,
          revisionSuggestion,
          rejectReason: reason || undefined,
          reviewer: "人工审核",
          createdAt,
          reviewedAt: now,
          durationMs: Date.now() - createdMs,
        };
        return [newRecord, ...prev];
      });
      setSelectedPkg(null);
      setRejectReason("");
      setRejectError(false);
    },
    [selectedPkg, records, modalAnnotations, revisionSuggestion, rejectReason]
  );

  /** 拒绝入口：先校验驳回原因，必填才能驳回 */
  const confirmReject = useCallback(() => {
    if (!rejectReason.trim()) {
      setRejectError(true);
      return;
    }
    setRejectError(false);
    submitHumanDecision("rejected");
  }, [rejectReason, submitHumanDecision]);

  // 当前选中包对应的审核记录
  const selectedRecord = useMemo(() => {
    if (!selectedPkg) return null;
    return records.find((r) => r.packageId === selectedPkg.id) || null;
  }, [selectedPkg, records]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30">
            <FileSearch size={13} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">内容审核工作流</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Content Review · 待审 {pendingItems.length} · 通过率 {stats.passRate}% · SLA {slaHours}h
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowSlaConfig((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors"
          >
            <Settings size={11} className="text-amber-400" />
            <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">SLA</span>
          </button>
          <button
            onClick={loadPackages}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
          >
            {refreshing ? (
              <Loader2 size={11} className="text-emerald-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-emerald-400" />
            )}
            <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">
              {refreshing ? "刷新中..." : "刷新"}
            </span>
          </button>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className="px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06] text-red-300 text-[10px] font-mono">
            {error}
          </div>
        </div>
      )}

      {/* SLA 配置面板 */}
      <AnimatePresence>
        {showSlaConfig && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-b border-white/[0.04]"
          >
            <div className="p-4 bg-white/[0.01]">
              <div className="flex items-center gap-2 mb-2">
                <Timer size={11} className="text-amber-400" />
                <span className="text-[10px] font-mono text-amber-400 uppercase tracking-wider">
                  审核 SLA 配置
                </span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={72}
                  step={1}
                  value={slaHours}
                  onChange={(e) => setSlaHours(Number(e.target.value))}
                  className="flex-1 accent-amber-500"
                />
                <span className="text-[12px] font-mono font-bold text-amber-300 w-16 text-right">
                  {slaHours} 小时
                </span>
              </div>
              <p className="text-[9px] font-mono text-gray-600 mt-1">
                超过 SLA 时长的待审核内容将被标记为超时
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-emerald-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载待审核内容...</p>
        </div>
      ) : (
        <>
          {/* 审核统计卡片 */}
          <div className="grid grid-cols-4 gap-2 p-4 border-b border-white/[0.04]">
            <KpiCard icon={History} label="今日审核" value={String(stats.todayCount)} color="#34D399" />
            <KpiCard icon={TrendingUp} label="通过率" value={`${stats.passRate}%`} color="#38BDF8" />
            <KpiCard icon={Clock} label="平均耗时" value={stats.avgDurationLabel} color="#A78BFA" />
            <KpiCard icon={AlertTriangle} label="超时数" value={String(stats.overdueCount)} color="#F87171" />
          </div>

          {/* 待审核列表 */}
          <div className="p-4 border-b border-white/[0.04]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileSearch size={11} className="text-emerald-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  待审核列表（{pendingItems.length}）
                </span>
              </div>
              <button
                onClick={() => void batchAiReview()}
                disabled={pendingItems.filter((i) => i.status === "pending").length === 0}
                className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles size={10} />
                批量 AI 初审
              </button>
            </div>
            {pendingItems.length === 0 ? (
              <div className="py-8 text-center">
                <FileSearch size={22} className="mx-auto text-gray-600 mb-2" />
                <p className="text-[11px] font-mono text-gray-500">暂无待审核内容</p>
                <p className="text-[9px] font-mono text-gray-700 mt-1">
                  生成内容完成后将自动进入审核队列
                </p>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
                {pendingItems.map((item) => {
                  const sla = slaRemaining(item.pkg.createdAt, slaHours);
                  const aiScore = item.record?.aiReview?.overallScore;
                  return (
                    <div
                      key={item.pkg.id}
                      className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5 hover:bg-white/[0.04] transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        {/* 状态指示条 */}
                        <div
                          className="flex-shrink-0 w-1.5 h-10 rounded-full"
                          style={{ background: STATUS_COLOR[item.status] }}
                        />
                        {/* 主体信息 */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[12px] text-gray-200 truncate">
                              {item.pkg.topic || "未命名"}
                            </span>
                            {item.status === "ai_reviewing" && (
                              <Loader2 size={10} className="text-sky-400 animate-spin flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className="px-1 rounded text-[8px] font-mono bg-sky-500/15 text-sky-300">
                              {detectPlatform(item.pkg)}
                            </span>
                            <span className="text-[9px] font-mono text-gray-500">
                              {item.pkg.createdAt?.slice(0, 16).replace("T", " ") || "未知时间"}
                            </span>
                            <span
                              className="px-1 rounded text-[8px] font-mono"
                              style={{
                                background: `${STATUS_COLOR[item.status]}20`,
                                color: STATUS_COLOR[item.status],
                              }}
                            >
                              {STATUS_LABEL[item.status]}
                            </span>
                            {aiScore !== undefined && (
                              <span className="text-[8px] font-mono text-gray-500">
                                AI {aiScore}/100
                              </span>
                            )}
                          </div>
                        </div>
                        {/* SLA 倒计时 */}
                        <div className="flex-shrink-0 text-right">
                          <div className="text-[8px] font-mono text-gray-600 uppercase">SLA</div>
                          <div
                            className="text-[10px] font-mono font-bold"
                            style={{ color: sla.overdue ? "#F87171" : "#FBBF24" }}
                          >
                            {sla.label}
                          </div>
                        </div>
                        {/* 操作按钮 */}
                        <div className="flex-shrink-0 flex items-center gap-1">
                          {item.status === "pending" && (
                            <button
                              onClick={() => void triggerAiReview(item.pkg)}
                              className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors"
                            >
                              <Sparkles size={10} />
                              AI 初审
                            </button>
                          )}
                          {item.status === "ai_reviewing" && (
                            <span className="text-[9px] font-mono text-sky-400">分析中...</span>
                          )}
                          {(item.status === "ai_passed" ||
                            item.status === "ai_rejected" ||
                            item.status === "human_reviewing") && (
                            <button
                              onClick={() => openReviewModal(item.pkg)}
                              className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                            >
                              <MessageSquare size={10} />
                              复审
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 审核历史记录 */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <History size={11} className="text-violet-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  审核历史（{historyItems.length}）
                </span>
              </div>
              <div className="relative">
                <Search size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-600" />
                <input
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="搜索主题/平台"
                  className="pl-6 pr-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-violet-500/40 w-32"
                />
              </div>
            </div>
            {/* 历史状态筛选 */}
            <div className="flex items-center gap-1 mb-2">
              <Filter size={10} className="text-gray-500" />
              {(
                [
                  { id: "all", label: "全部" },
                  { id: "approved", label: "已通过" },
                  { id: "rejected", label: "已拒绝" },
                ] as const
              ).map((f) => (
                <button
                  key={f.id}
                  onClick={() => setHistoryFilter(f.id as ReviewStatus | "all")}
                  className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
                    historyFilter === f.id
                      ? "bg-violet-500/15 text-violet-300 border border-violet-500/30"
                      : "text-gray-500 hover:text-gray-300 border border-transparent"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {historyItems.length === 0 ? (
              <div className="py-6 text-center">
                <History size={20} className="mx-auto text-gray-600 mb-2" />
                <p className="text-[10px] font-mono text-gray-600">暂无审核历史</p>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
                {historyItems.map((r) => {
                  const wasOverdue =
                    r.reviewedAt
                      ? new Date(r.reviewedAt).getTime() >
                        new Date(r.createdAt).getTime() + slaHours * 3600000
                      : slaRemaining(r.createdAt, slaHours).overdue;
                  return (
                    <div
                      key={r.id}
                      className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5"
                    >
                      <div className="flex items-center gap-2.5">
                        {r.decision === "approved" ? (
                          <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />
                        ) : (
                          <XCircle size={14} className="text-red-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] text-gray-200 truncate">{r.topic}</div>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className="px-1 rounded text-[8px] font-mono bg-sky-500/15 text-sky-300">
                              {r.platform}
                            </span>
                            <span className="text-[9px] font-mono text-gray-500">
                              {r.reviewedAt?.slice(0, 16).replace("T", " ") || "未完成"}
                            </span>
                            {r.durationMs !== undefined && (
                              <span className="text-[9px] font-mono text-gray-500">
                                耗时 {formatDuration(r.durationMs)}
                              </span>
                            )}
                            {wasOverdue && (
                              <span className="flex items-center gap-0.5 text-[8px] font-mono text-red-400">
                                <AlertTriangle size={8} />
                                SLA 超时
                              </span>
                            )}
                            {r.annotations.length > 0 && (
                              <span className="flex items-center gap-0.5 text-[8px] font-mono text-gray-500">
                                <MessageSquare size={8} />
                                {r.annotations.length} 批注
                              </span>
                            )}
                          </div>
                        </div>
                        <span
                          className="px-1.5 py-0.5 rounded text-[8px] font-mono"
                          style={{
                            background: `${STATUS_COLOR[r.status]}20`,
                            color: STATUS_COLOR[r.status],
                          }}
                        >
                          {STATUS_LABEL[r.status]}
                        </span>
                      </div>
                      {r.revisionSuggestion && (
                        <div className="mt-1.5 px-2 py-1 rounded text-[9px] font-mono text-gray-400 bg-white/[0.02] border border-white/[0.04]">
                          修改建议：{r.revisionSuggestion}
                        </div>
                      )}
                      {r.rejectReason && (
                        <div className="mt-1.5 px-2 py-1 rounded text-[9px] font-mono text-red-300/80 bg-red-500/[0.04] border border-red-500/10">
                          驳回原因：{r.rejectReason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* 审核详情 Modal */}
      <AnimatePresence>
        {selectedPkg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setSelectedPkg(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#131826] to-[#0a0e1a] backdrop-blur-md flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={14} className="text-emerald-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-white truncate">
                      {selectedPkg.topic || "未命名"}
                    </div>
                    <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
                      {detectPlatform(selectedPkg)} ·{" "}
                      {selectedPkg.createdAt?.slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedPkg(null)}
                  className="p-1 rounded-md hover:bg-white/[0.05] transition-colors"
                >
                  <X size={14} className="text-gray-400" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* 内容预览 */}
                <section>
                  <SectionTitle icon={FileText} label="内容预览" color="#38BDF8" />
                  <div className="mt-2 rounded-md border border-white/[0.04] bg-white/[0.02] p-3 max-h-[160px] overflow-y-auto">
                    <pre className="text-[10px] font-mono text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                      {extractScriptContent(selectedPkg) || "（无脚本内容）"}
                    </pre>
                  </div>
                </section>

                {/* AI 初审结果（合规 / 质量 / 品牌一致性） */}
                <section>
                  <SectionTitle icon={Sparkles} label="AI 初审结果" color="#A78BFA" />
                  {selectedRecord?.aiReview ? (
                    <div className="mt-2 space-y-2">
                      <AiCheckCard item={selectedRecord.aiReview.compliance} />
                      <AiCheckCard item={selectedRecord.aiReview.quality} />
                      <AiCheckCard item={selectedRecord.aiReview.brand} />
                      <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-violet-500/20 bg-violet-500/[0.04]">
                        <Gauge size={12} className="text-violet-400" />
                        <span className="text-[10px] font-mono text-gray-300">
                          {selectedRecord.aiReview.summary}
                        </span>
                        <span className="ml-auto text-[12px] font-mono font-bold text-violet-300">
                          {selectedRecord.aiReview.overallScore}/100
                        </span>
                      </div>
                    </div>
                  ) : selectedRecord?.status === "ai_reviewing" ? (
                    <div className="mt-2 px-3 py-3 rounded-md border border-white/[0.04] bg-white/[0.02] text-center">
                      <Loader2 size={16} className="mx-auto text-sky-400 animate-spin mb-2" />
                      <p className="text-[10px] font-mono text-sky-400">AI 初审分析中...</p>
                    </div>
                  ) : (
                    <div className="mt-2 px-3 py-3 rounded-md border border-white/[0.04] bg-white/[0.02] text-center">
                      <p className="text-[10px] font-mono text-gray-500">尚未进行 AI 初审</p>
                      <button
                        onClick={() => void triggerAiReview(selectedPkg)}
                        className="mt-2 flex items-center gap-1 mx-auto px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors"
                      >
                        <Sparkles size={10} />
                        启动 AI 初审
                      </button>
                    </div>
                  )}
                </section>

                {/* 修改痕迹（批注列表，可追溯） */}
                <section>
                  <SectionTitle
                    icon={PenLine}
                    label={`修改痕迹（${modalAnnotations.length}）`}
                    color="#FBBF24"
                  />
                  <div className="mt-2 space-y-1.5">
                    {modalAnnotations.length === 0 ? (
                      <p className="text-[10px] font-mono text-gray-600 px-2 py-1">暂无批注</p>
                    ) : (
                      modalAnnotations.map((a) => (
                        <div
                          key={a.id}
                          className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2"
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="px-1 rounded text-[8px] font-mono bg-amber-500/15 text-amber-300">
                              {a.field}
                            </span>
                            <span className="text-[9px] font-mono text-gray-600">
                              {a.createdAt.slice(11, 16)}
                            </span>
                            <button
                              onClick={() => removeAnnotation(a.id)}
                              className="ml-auto text-gray-600 hover:text-red-400"
                            >
                              <X size={10} />
                            </button>
                          </div>
                          <p className="text-[10px] text-gray-300 mt-1">{a.content}</p>
                          {a.suggestion && (
                            <p className="text-[9px] font-mono text-amber-300/80 mt-1">
                              建议：{a.suggestion}
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  {/* 新增批注表单 */}
                  <div className="mt-2 grid grid-cols-3 gap-1.5">
                    <input
                      value={annoField}
                      onChange={(e) => setAnnoField(e.target.value)}
                      placeholder="字段/位置"
                      className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-amber-500/40"
                    />
                    <input
                      value={annoContent}
                      onChange={(e) => setAnnoContent(e.target.value)}
                      placeholder="批注内容 *"
                      className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-amber-500/40"
                    />
                    <input
                      value={annoSuggestion}
                      onChange={(e) => setAnnoSuggestion(e.target.value)}
                      placeholder="修改建议"
                      className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-amber-500/40"
                    />
                  </div>
                  <button
                    onClick={addAnnotation}
                    disabled={!annoContent.trim()}
                    className="mt-1.5 flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <PenLine size={10} />
                    添加批注
                  </button>
                </section>

                {/* 整体修改建议 */}
                <section>
                  <SectionTitle icon={MessageSquare} label="整体修改建议" color="#34D399" />
                  <textarea
                    value={revisionSuggestion}
                    onChange={(e) => setRevisionSuggestion(e.target.value)}
                    placeholder="输入整体修改建议（可选，将作为修改痕迹留痕）"
                    rows={2}
                    className="mt-2 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40 resize-none"
                  />
                </section>

                {/* 驳回原因（P12 短板2：拒绝必填，留痕可追溯） */}
                <section>
                  <SectionTitle icon={XCircle} label="驳回原因" color="#F87171" />
                  <textarea
                    value={rejectReason}
                    onChange={(e) => {
                      setRejectReason(e.target.value);
                      if (e.target.value.trim()) setRejectError(false);
                    }}
                    placeholder="驳回原因（选择拒绝时必填，将记入审核留痕）"
                    rows={2}
                    className={`mt-2 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border text-[10px] text-gray-200 font-mono focus:outline-none resize-none ${
                      rejectError
                        ? "border-red-500/60 ring-1 ring-red-500/30"
                        : "border-white/[0.06] focus:border-red-500/40"
                    }`}
                  />
                  {rejectError && (
                    <p className="mt-1 text-[9px] font-mono text-red-400">
                      请先填写驳回原因，驳回操作才会生效
                    </p>
                  )}
                </section>
              </div>

              {/* Modal Footer：人工复审决策按钮 */}
              <div className="p-4 border-t border-white/[0.06] flex items-center justify-between gap-2">
                <div className="text-[9px] font-mono text-gray-600">
                  {selectedRecord?.aiReview
                    ? `AI 综合得分 ${selectedRecord.aiReview.overallScore}/100`
                    : "建议先完成 AI 初审"}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={confirmReject}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25 transition-colors"
                  >
                    <XCircle size={12} />
                    拒绝
                  </button>
                  <button
                    onClick={() => submitHumanDecision("approved")}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                  >
                    <CheckCircle2 size={12} />
                    通过
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// KPI 小卡片
// ============================================
interface KpiCardProps {
  icon: typeof History;
  label: string;
  value: string;
  color: string;
}

function KpiCard({ icon: Icon, label, value, color }: KpiCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">{label}</div>
        <div className="text-[13px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 区块标题
// ============================================
function SectionTitle({
  icon: Icon,
  label,
  color,
}: {
  icon: typeof History;
  label: string;
  color: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon size={12} style={{ color }} />
      <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

// ============================================
// AI 检查项卡片
// ============================================
function AiCheckCard({ item }: { item: AiCheckItem }) {
  const color = CHECK_STATUS_COLOR[item.status];
  return (
    <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          {item.status === "pass" ? (
            <ShieldCheck size={11} style={{ color }} />
          ) : (
            <AlertTriangle size={11} style={{ color }} />
          )}
          <span className="text-[10px] text-gray-300">{item.name}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="text-[8px] font-mono px-1 rounded"
            style={{ background: `${color}20`, color }}
          >
            {CHECK_STATUS_LABEL[item.status]}
          </span>
          <span className="text-[10px] font-mono font-bold" style={{ color }}>
            {item.score}/100
          </span>
        </div>
      </div>
      <div className="h-1 rounded-full bg-white/[0.05] overflow-hidden mb-1">
        <div className="h-full rounded-full" style={{ width: `${item.score}%`, background: color }} />
      </div>
      <div className="text-[9px] font-mono text-gray-600">{item.detail}</div>
      {item.issues.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {item.issues.map((iss, i) => (
            <span key={i} className="px-1 rounded text-[8px] font-mono bg-red-500/10 text-red-300/80">
              {iss}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}